import type { ChangeEvent, KeyboardEvent } from 'react';
import { useState } from 'react';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import Icon from '@/components/base/Icon';
import type { InputProps } from '@/components/base/Input';
// Components
import Input from '@/components/base/Input';
import ActionTag from '@/components/base/Tag/ActionTag';

type eventOnKeyDownType = KeyboardEvent<HTMLInputElement> & { target: HTMLInputElement };

export type InputActionTagProps<TTag> = InputProps & {
    onDeleteItem: (arg: TTag) => void;
    triggerEventList?: KeyboardEvent<HTMLInputElement>['key'][];
    tagList: TTag[];
    shouldClearSpace?: boolean;
    onAddItem: (arg: HTMLInputElement['value']) => void;
    additionalCheckOnEvent?: (arg: ChangeEvent<HTMLInputElement> | eventOnKeyDownType) => boolean;
};

const InputActionTag = <TTag extends string>({
    onAddItem,
    onDeleteItem,
    triggerEventList = [],
    tagList,
    shouldClearSpace = false,
    additionalCheckOnEvent = () => true,
    ...restProps
}: InputActionTagProps<TTag>) => {
    const { t } = useTranslation('commons');
    const [isFilled, setIsFilled] = useState(false);
    return (
        <>
            <Input
                type="text"
                data-testid="input-action-tag-add"
                onChange={(e) => {
                    if (e.target.value?.trim() !== '' && additionalCheckOnEvent(e)) {
                        if (!isFilled) {
                            setIsFilled(true);
                        }
                    } else {
                        //clear all space in string
                        if (shouldClearSpace) {
                            e.target.value = e.target.value?.replaceAll(' ', '');
                        }
                        setIsFilled(false);
                    }
                }}
                afterInput={
                    <span className="text-xs text-icoGray-400 dark:text-icoDarkMode-wolf">
                        {isFilled ? t('press_enter_to_add') : null}
                    </span>
                }
                onKeyDown={(e: eventOnKeyDownType) => {
                    if (
                        e.target.value?.trim() !== '' &&
                        additionalCheckOnEvent(e) &&
                        (triggerEventList.includes(e.key.toLowerCase()) ||
                            e.key.toLowerCase() === 'enter')
                    ) {
                        e.preventDefault();
                        onAddItem(e.target.value);
                        e.target.value = '';
                        setIsFilled(false);
                    }
                }}
                {...restProps}
            />
            {tagList.length ? (
                <ul className="-ml-2 mt-3 flex flex-wrap">
                    {tagList.map((tag) => {
                        return (
                            <li className="mb-2 ml-2" key={tag}>
                                <ActionTag data-testid="input-action-tag">
                                    <span className="mr-1">{tag}</span>
                                    <button
                                        type="button"
                                        aria-label="input-action-tag-delete"
                                        className="leading-none"
                                        data-testid="input-action-tag-delete"
                                        onClick={() => onDeleteItem(tag)}>
                                        <Icon icon="close" className="align-bottom" />
                                    </button>
                                </ActionTag>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </>
    );
};

export default InputActionTag;
